import { IAccountDetails } from "@/Common/Components/Portal/InvitationPopup/AccountDetails/AccountDetails";
import { getAccountDetails } from "@/Common/Components/Portal/InvitationPopup/Api/InvitationApiClient";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { PopulationBasedFundingView } from "@/Patient/Components/PopulationBasedFunding/PopulationBasedFundingView";
import { Relations } from "@/Patient/Components/Profile/Relations/Relations";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { PatientEnrollment } from "./PatientEnrollment/PatientEnrollment";
import { CliniciansEdit } from "./CliniciansEdit";
import { CliniciansView } from "./CliniciansView";
import { ContactsEdit } from "./ContactsEdit";
import { ContactsView } from "./ContactsView";
import { DemographicEdit } from "./DemographicEdit";
import { DemographicView } from "./DemographicView";
import { IdentifiersEdit } from "./IdentifiersEdit";
import { IdentifiersView } from "./IdentifiersView";
import { OtherEdit } from "./OtherEdit";
import { OtherViewFixed } from "./OtherViewFixed";
import { PortalManagement } from "./PortalManagement";

import "./ProfileView.css";

interface IPatientProfileProps {
    Model: IPatientUIModel;
}

class ProfileViewState {
    public accountDetailsV2?: IAccountDetails;
}

class ProfileView extends React.Component<IPatientProfileProps, ProfileViewState> {
    constructor(props: IPatientProfileProps) {
        super(props);

        this.state = {
            accountDetailsV2: null
        };
    }

    public componentDidMount(): void {
        const currentPatientContactId = this.props.Model.get("PatientContactId");

        const showPortalTab = this.shouldShowPortalTab(currentPatientContactId);

        if (showPortalTab) {
            this.loadPortalAccountDetails(currentPatientContactId);
        }
    }

    public componentDidUpdate(prevProps: IPatientProfileProps): void {
        const currentPatientContactId = this.props.Model.get("PatientContactId");

        if (prevProps.Model.get("PatientContactId") !== currentPatientContactId) {
            const showPortalTab = this.shouldShowPortalTab(currentPatientContactId);

            if (showPortalTab) {
                this.loadPortalAccountDetails(currentPatientContactId);
            }
        }
    }

    public render(): JSX.Element {
        const currentPatientContactId = this.props.Model.get("PatientContactId");
        const showPortalTab = this.shouldShowPortalTab(currentPatientContactId);

        const currentEmployee = sessionStore.getCurrentEmployee();
        const shouldShowEnrollmentTab = !cacheStore.ClinicSettings.EnablePopulationBasedFunding && (currentEmployee.isActiveSiteOntario() || cacheStore.SpecificClientSettings.HasMedfarRAMQ);

        return (
            <div className="profileView">
                <div className="patient-subtabs">
                    <ul>
                        {this.buildProfileTab(ProfileTab.Demographic, "DemographicLabel")}
                        {this.buildProfileTab(ProfileTab.Identifiers, "IdentifiersLabel")}
                        {this.buildProfileTab(ProfileTab.Contacts, "ContactsLabel")}
                        {this.buildProfileTab(ProfileTab.Clinicians, cacheStore.ClinicSettings.EnablePatientChartAllowlist ? "CareTeamLabel" : "CliniciansLabel")}
                        {this.buildProfileTab(ProfileTab.Other, "OtherLabel")}
                        {this.buildProfileTab(ProfileTab.Relationships, "RelationsLabel")}

                        {shouldShowEnrollmentTab &&
                            this.buildProfileTab(ProfileTab.Enrollment, "EnrolmentLabel")
                        }
                        {sessionStore.getCurrentEmployee().isActiveSiteQuebec() && this.buildProfileTab(ProfileTab.DSQ, "DSQLabel")}

                        {showPortalTab &&
                            this.buildProfileTab(ProfileTab.PortalSubscription, "PortalLabel")
                        }
                        {cacheStore.ClinicSettings.EnablePopulationBasedFunding &&
                            this.buildProfileTab(ProfileTab.PopulationBasedFunding, "PublicBasedFundingAcronymLabel")
                        }
                    </ul>
                </div>
                <div className="profile-subtabs-container">
                    {this.switchTabComponents(this.props.Model.get("PatientProfileActiveTab"))}
                </div>
            </div>
        );
    }

    private buildProfileTab(tab: ProfileTab, labelKey: string): JSX.Element {
        var css = this.props.Model.get("PatientProfileActiveTab") === tab ? "active" : null;
        return <li className={css} data-cy={`patientProfile-tab${ProfileTab[tab]}`}><a onClick={this.onTabClick.bind(this, tab)}>{cacheStore.GetString(labelKey)}</a></li>;
    }

    private onTabClick(tab: ProfileTab): void {
        var operation = new LocalEvent(LocalPatientOperations.Profile.ChangeProfileTabs);
        operation.AddItem("value", tab);
        LocalEventDispatcher.dispatch(operation);
    }

    private switchTabComponents(current: ProfileTab): JSX.Element {
        var currentPatientContactId = this.props.Model.get("PatientContactId");

        const focusOperation = this.props.Model.get("PatientBannerFocusOperation");

        switch (current) {
            case ProfileTab.Demographic:
            {
                let model = patientStore.PatientProfileDemographicModels[currentPatientContactId];
                let countryLookupId = "";
                var patient = patientStore.PatientModels[currentPatientContactId];

                if (!(patient == null))
                    countryLookupId = patient.GetCountryLookupId();

                if (this.props.Model.get("PatientProfileEditMode"))
                    return <DemographicEdit model={model} countryLookupId={countryLookupId} autofocusOperation={focusOperation} />;
                else
                    return <DemographicView model={model} countryLookupId={countryLookupId} />;
            }

            case ProfileTab.Identifiers:
            {
                let model = patientStore.PatientProfileIdentifiersModels[currentPatientContactId];

                if (this.props.Model.get("PatientProfileEditMode"))
                    return <IdentifiersEdit Model={model} AutofocusOperation={focusOperation} />;
                else
                    return <IdentifiersView Model={model} />;
            }

            case ProfileTab.Contacts:
            {
                let model = patientStore.PatientProfileContactsModels[currentPatientContactId];

                const focusOperationTarget = this.props.Model.get("PatientBannerFocusOperationTarget");

                if (this.props.Model.get("PatientProfileEditMode"))
                    return (
                        <ContactsEdit
                            model={model}
                            autofocusOperation={focusOperation}
                            autofocusId={focusOperationTarget}
                        />
                    );
                else
                    return (
                        <ContactsView
                            model={model}
                        />
                    );
            }

            case ProfileTab.Clinicians:
            {
                let model = patientStore.PatientProfileCliniciansModels[currentPatientContactId];

                if (this.props.Model.get("PatientProfileEditMode"))
                    return <CliniciansEdit Model={model} />;
                else
                    return <CliniciansView Model={model} />;
            }

            case ProfileTab.Other:
            {
                let model = patientStore.PatientProfileOtherIdentificationModels[currentPatientContactId];
                let countryLookupId = "";
                var patient = patientStore.PatientModels[currentPatientContactId];
                if (!(patient == null))
                    countryLookupId = patient.GetCountryLookupId();

                if (this.props.Model.get("PatientProfileEditMode"))
                    return <OtherEdit model={model} PatientCountryId={countryLookupId} />;
                else
                    return <OtherViewFixed model={model} PatientCountryId={countryLookupId} />;
            }

            case ProfileTab.Relationships:
            {
                let model = patientStore.PatientProfileRelationshipsModels[currentPatientContactId];
                let patientModel = patientStore.PatientModels[currentPatientContactId];

                return (
                    <Relations
                        model={model}
                        patientModel={patientModel}
                        view={this.props.Model.get("PatientProfileEditMode")}
                    />
                );
            }

            case ProfileTab.Enrollment:
            {
                const quebecModel = patientStore.PatientProfileEnrollmentModels[currentPatientContactId];
                const patientModel = patientStore.PatientModels[currentPatientContactId];

                return (
                    <PatientEnrollment
                        patientId={patientModel.PatientResponse.ContactFacet_id}
                        quebecModel={quebecModel}
                        activeSite={sessionStore.getCurrentEmployee().getActiveSite()}
                        clinicSettings={cacheStore.ClinicSettings}
                        getPhysician={contactFacetId => cacheStore.GetCliniciansPersonsByContactFacetId(contactFacetId)}
                    />
                );
            }

            case ProfileTab.DSQ:
                return <div />;

            case ProfileTab.PortalSubscription:
                return (
                    <PortalManagement
                        patientInfo={patientStore.PatientModels[currentPatientContactId]}
                        profileModel={patientStore.PatientPortalOverviewModels[currentPatientContactId]}
                        patientId={currentPatientContactId}
                        accountDetailsV2={this.state.accountDetailsV2}
                    />
                );

            case ProfileTab.PopulationBasedFunding:
            {
                return (
                    <PopulationBasedFundingView patientModel={patientStore.PatientModels[currentPatientContactId]} />
                );
            }

            default:
                return <div />;
        }
    }

    private shouldShowPortalTab(patientId: string): boolean {
        const personInfo = patientStore.PatientModels[patientId];

        return cacheStore.ClinicSettings_Portal.EnablePortalWebsite
            && personInfo?.IndividualResponse
            && (
                personInfo.IndividualResponse.PortalSyncState === PortalRegistrationState.RegistrationComplete
                || personInfo.IndividualResponse.PortalSyncState === PortalRegistrationState.RegistrationPending
                || personInfo.IndividualResponse.PortalSyncState === PortalRegistrationState.ChildAccount
                || personInfo.IndividualResponse.PortalSyncState === PortalRegistrationState.InvitationLocked
            );
    }

    private async loadPortalAccountDetails(patientId: string): Promise<void> {
        patientStore.ActionsPortalManagement.loadPortalAccountOverview(patientId);

        if (cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) {
            const detailsV2 = await getAccountDetails(patientId);
            const accountDetailsV2 = {
                username: detailsV2.Username,
                lastInvitationDate: detailsV2.LastInvitationDate ? new Date(detailsV2.LastInvitationDate) : null,
                lastLoginAttemptDate: detailsV2.LastLoginAttemptDate ? new Date(detailsV2.LastLoginAttemptDate) : null,
                lastLoginDate: detailsV2.LastLoginDate ? new Date(detailsV2.LastLoginDate) : null,
                refusalDate: detailsV2.RefusalDate ? new Date(detailsV2.RefusalDate) : null,
                status: detailsV2.AccountStatus,
                isPopup: false
            };
            this.setState({ accountDetailsV2 });
        }
    }
}

export { ProfileView };
